import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {BLOCKS_CONFIG} from '../../blocks.config';
import {ControlContainer, FormControl, NgForm, ValidationErrors} from '@angular/forms';
import {SessionControllerService} from '../../../submission/src/app/session/session-controller.service';
import {PdfPartComponent} from '@blocks/pdf-part/pdf-part.component';
import {BlockComponent} from '@blocks/block/block.component';
import {LocalizationService} from '@shared/i18n/localization.service';
import {ContentLocale} from '@paperlessio/sdk/api/util';
import {RadioInput, RadioInputFormat, SelectOption} from '@paperlessio/sdk/api/models';

@Component({
  selector: 'app-radio-input',
  templateUrl: './radio-input.component.html',
  styleUrls: ['./radio-input.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class RadioInputComponent implements OnInit, OnChanges, OnDestroy {
  private localizationService = inject(LocalizationService);
  private form = inject(NgForm);
  private sessionController = inject(SessionControllerService, {optional: true});
  private pdfPartComponent = inject(PdfPartComponent, {optional: true});
  public blockComponent = inject(BlockComponent, {optional: true});
  public blocksConfig = inject(BLOCKS_CONFIG);

  @Input() block: RadioInput;

  @Input() data: number;
  @Output() dataChange: EventEmitter<number> = new EventEmitter<number>();

  @Input() slug: string;

  domId = '';
  RadioInputFormat = RadioInputFormat;

  formControl: FormControl<number | null>;
  ineligible = false;

  get options() {
    return this.block.children;
  }

  get isOverlay(): boolean {
    return !!this.pdfPartComponent;
  }

  ngOnInit() {
    if (this.blocksConfig.submittable) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    this.formControl = new FormControl<number | null>(this.data, (control: FormControl<number | null>) => this._valid(control));

    if (this.ineligible) {
      this.formControl.disable();
    }

    this.form.form.addControl(this.slug, this.formControl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if slug has changed, we need to update the form control
    if (changes.slug && this.formControl) {
      this.form.form.removeControl(changes.slug.previousValue);
      this.form.form.addControl(changes.slug.currentValue, this.formControl);
    }
  }

  ngOnDestroy(): void {
    this.form.form.removeControl(this.slug);
  }

  select(id: number) {
    if (!id) {
      return;
    }

    const opt = this.options?.find(o => o.id === id);

    if (!opt) {
      return;
    }

    this.data = opt.id;
    this.dataChange.emit(this.data);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.setValue(this.data, {emitViewToModelChange: false});
  }

  isChecked(option: SelectOption): boolean | string {
    return this.data === option.id ? 'checked' : false;
  }

  isValid(): boolean {
    if (this.block.settings.required && !this.ineligible) {
      return this.data > 0;
    } else {
      return true;
    }
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  private _valid(control: FormControl<number | null>): ValidationErrors | null {
    return this.isValid() ? null : {required: true};
  }
}
